import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Download, Instagram, Facebook, Twitter, Linkedin, Youtube } from 'lucide-react';
import { useSocialMediaOptimizer, type SocialPlatform, PLATFORM_PRESETS } from '@/hooks/useSocialMediaOptimizer';
import type { UploadedImage } from '@/pages/ImageToolsHub';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';

interface SocialMediaToolProps {
  images: UploadedImage[];
}

const PLATFORM_GROUPS = {
  Instagram: ['instagram-feed', 'instagram-story', 'instagram-reel'],
  Facebook: ['facebook-post', 'facebook-cover', 'facebook-ad'],
  Twitter: ['twitter-post', 'twitter-header'],
  LinkedIn: ['linkedin-post', 'linkedin-banner'],
  YouTube: ['youtube-thumbnail', 'youtube-banner'],
  Pinterest: ['pinterest-pin'],
};

export const SocialMediaTool: React.FC<SocialMediaToolProps> = ({ images }) => {
  const { isProcessing, progress, optimizedImages, optimizeForPlatforms, downloadAll } = useSocialMediaOptimizer();
  
  const [selectedPlatforms, setSelectedPlatforms] = useState<SocialPlatform[]>([]);
  const [showSafeZone, setShowSafeZone] = useState(false);

  const selectedImage = images.find(img => img.selected);

  const togglePlatform = (platform: SocialPlatform) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const selectAllInGroup = (group: SocialPlatform[]) => {
    const allSelected = group.every(p => selectedPlatforms.includes(p));
    if (allSelected) {
      setSelectedPlatforms(prev => prev.filter(p => !group.includes(p)));
    } else {
      setSelectedPlatforms(prev => [...new Set([...prev, ...group])]);
    }
  };

  const handleProcess = async () => {
    if (!selectedImage) {
      toast.error('Please select one image');
      return;
    }

    if (selectedPlatforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }

    await optimizeForPlatforms(selectedImage, selectedPlatforms, showSafeZone);
    toast.success(`Optimized for ${selectedPlatforms.length} platform(s)`);
  };

  const handleDownloadAll = () => {
    downloadAll(optimizedImages, 'social');
    toast.success('Downloaded all optimized images');
  };

  const getPlatformIcon = (groupName: string) => {
    switch (groupName) {
      case 'Instagram': return <Instagram className="h-4 w-4" />;
      case 'Facebook': return <Facebook className="h-4 w-4" />;
      case 'Twitter': return <Twitter className="h-4 w-4" />;
      case 'LinkedIn': return <Linkedin className="h-4 w-4" />;
      case 'YouTube': return <Youtube className="h-4 w-4" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Social Media Optimizer</CardTitle>
          <CardDescription>
            Select one image and choose platforms to optimize for
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!selectedImage && (
            <div className="p-4 border border-yellow-500 bg-yellow-500/10 rounded-lg">
              <p className="text-sm text-yellow-600 dark:text-yellow-400">
                Please select exactly one image from the library above
              </p>
            </div>
          )}

          {/* Safe Zone Option */}
          <div className="flex items-center justify-between">
            <div>
              <Label>Show Safe Zone Guides</Label>
              <p className="text-xs text-muted-foreground">
                Display red guides for important content areas
              </p>
            </div>
            <Switch
              checked={showSafeZone}
              onCheckedChange={setShowSafeZone}
            />
          </div>

          {/* Platform Selection */}
          <div className="space-y-4">
            <Label>Select Platforms</Label>
            {Object.entries(PLATFORM_GROUPS).map(([groupName, platforms]) => (
              <div key={groupName} className="space-y-2">
                <div className="flex items-center gap-2">
                  {getPlatformIcon(groupName)}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => selectAllInGroup(platforms as SocialPlatform[])}
                    className="text-xs"
                  >
                    {platforms.every(p => selectedPlatforms.includes(p as SocialPlatform))
                      ? `Deselect All ${groupName}`
                      : `Select All ${groupName}`}
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-2 ml-6">
                  {platforms.map((platform) => {
                    const preset = PLATFORM_PRESETS.find(p => p.platform === platform);
                    if (!preset) return null;

                    return (
                      <div key={platform} className="flex items-center space-x-2">
                        <Checkbox
                          id={platform}
                          checked={selectedPlatforms.includes(platform as SocialPlatform)}
                          onCheckedChange={() => togglePlatform(platform as SocialPlatform)}
                        />
                        <Label htmlFor={platform} className="text-sm cursor-pointer">
                          {preset.name} ({preset.width}x{preset.height})
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Process Button */}
          <Button
            onClick={handleProcess}
            disabled={isProcessing || !selectedImage || selectedPlatforms.length === 0}
            className="w-full"
          >
            {isProcessing ? 'Optimizing...' : `Optimize for ${selectedPlatforms.length} Platform(s)`}
          </Button>

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground text-center">{Math.round(progress)}%</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {optimizedImages.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Optimized Images</CardTitle>
                <CardDescription>{optimizedImages.length} versions created</CardDescription>
              </div>
              <Button onClick={handleDownloadAll}>
                <Download className="h-4 w-4 mr-2" />
                Download All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {optimizedImages.map((image) => (
              <div key={image.id} className="space-y-2">
                <div className="text-sm font-medium">{image.preset.name}</div>
                <div className="text-xs text-muted-foreground">
                  {image.preset.width}x{image.preset.height}
                </div>
                <img
                  src={image.optimized}
                  alt={image.preset.name}
                  className="w-full h-auto border rounded"
                />
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
